/**
 * PortfolioHeatmapView — people × weeks committed-FTE heat-map.
 *
 * Y-axis: every active named_resource (sorted by peak load desc).
 * X-axis: the selected window of weeks.
 * Cell: TOTAL committed FTE% that week across ALL live projects (explicit
 *       Phase-2 assignments only). Cells over 100% are over-allocation flags.
 *   • blocked PTO → diagonal-striped grey
 *   • 0%          → idle slate
 *   • ≤50%        → light emerald
 *   • ≤85%        → healthy emerald
 *   • ≤100%       → loaded amber
 *   • >100%       → rose (over-allocated)
 *
 * Hovering a committed cell reveals the per-project FTE% breakdown that
 * makes up the total, so managers can see WHO/WHAT is over-booking a person.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Loader2, Flame, AlertTriangle, Download, RefreshCw,
  Users as UsersIcon, CalendarOff, CalendarRange, Layers, Mail, Sparkles, FileDown,
} from 'lucide-react';
import SuggestReassignmentDialog from '../components/SuggestReassignmentDialog';
import { EntityRegionFilterChips, useEntityRegionOptions } from '../components/EntityRegionFilterChips';
import { exportCapacityReport } from '../lib/capacityReportPdf';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cell colour for a total committed FTE percentage.
const cellStyleFor = ({ blocked, pct }) => {
  if (blocked) {
    return {
      bg: 'bg-slate-200',
      border: 'border-slate-300',
      text: 'text-slate-400',
      pattern: 'bg-[repeating-linear-gradient(45deg,#cbd5e1_0,#cbd5e1_2px,transparent_2px,transparent_6px)]',
      label: 'PTO',
    };
  }
  if (pct <= 0.5) return { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-400', label: 'Idle' };
  if (pct <= 50) return { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', label: 'Light' };
  if (pct <= 85) return { bg: 'bg-emerald-200', border: 'border-emerald-300', text: 'text-emerald-800', label: 'Healthy' };
  if (pct <= 100) return { bg: 'bg-amber-200', border: 'border-amber-300', text: 'text-amber-900', label: 'Loaded' };
  if (pct <= 150) return { bg: 'bg-rose-300', border: 'border-rose-400', text: 'text-rose-900', label: 'Over-allocated' };
  return { bg: 'bg-rose-500', border: 'border-rose-600', text: 'text-white', label: 'Severe' };
};

const fmtWeekLabel = (iso) => {
  try {
    const d = new Date(iso + 'T00:00:00Z');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  } catch { return iso; }
};

export default function PortfolioHeatmapView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weeks, setWeeks] = useState(26);
  const [hoverCell, setHoverCell] = useState(null);
  const [reassignTarget, setReassignTarget] = useState(null); // { resourceId, weekIso } | null
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [rangeMode, setRangeMode] = useState('preset');
  // v380.1 — Entity / Region chip filters (same behaviour as Workspaces),
  // remembered per browser between visits.
  const [entityFilter, setEntityFilter] = useState(() => {
    try { return JSON.parse(localStorage.getItem('bp-resource-erf') || '{}').e || []; } catch { return []; }
  });
  const [regionFilter, setRegionFilter] = useState(() => {
    try { return JSON.parse(localStorage.getItem('bp-resource-erf') || '{}').r || []; } catch { return []; }
  });
  useEffect(() => {
    localStorage.setItem('bp-resource-erf', JSON.stringify({ e: entityFilter, r: regionFilter }));
  }, [entityFilter, regionFilter]);
  const { entities: erEntities, regions: erRegions } = useEntityRegionOptions();

  const load = (opts = {}) => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (opts.mode === 'custom' || (rangeMode === 'custom' && !opts.mode)) {
      const s = opts.start ?? customStart;
      const e = opts.end ?? customEnd;
      if (s) params.set('start_date', s);
      if (e) params.set('end_date', e);
      if (!s && !e) params.set('weeks', String(opts.weeks ?? weeks));
    } else {
      params.set('weeks', String(opts.weeks ?? weeks));
    }
    if (entityFilter.length) params.set('entity', entityFilter.join(','));
    if (regionFilter.length) params.set('region', regionFilter.join(','));
    axios.get(`${API_URL}/api/portfolio/resource-heatmap?${params.toString()}`, { withCredentials: true })
      .then((r) => { setData(r.data); setLoading(false); })
      .catch((err) => {
        const detail = err?.response?.data?.detail || err?.message || 'Failed to load';
        setError(detail);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (rangeMode === 'preset') load({ weeks });
    else if (customStart && customEnd) load({ mode: 'custom' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeks, rangeMode, entityFilter, regionFilter]);

  const applyCustomRange = () => {
    if (!customStart || !customEnd) return;
    load({ mode: 'custom', start: customStart, end: customEnd });
  };

  const roleLabels = data?.role_labels || {};
  const summary = data?.summary || {};

  const resources = useMemo(() => data?.resources || [], [data]);

  const onExportCsv = () => {
    if (!resources.length) return;
    const lines = [];
    const header = ['name', 'email', 'roles', 'peak_pct', ...data.week_labels];
    lines.push(header.join(','));
    for (const r of resources) {
      const row = [
        r.name?.replace(/,/g, ' ') ?? '',
        r.email ?? '',
        (r.roles || []).join(' '),
        r.peak_pct ?? '',
        ...r.weekly_fte_pct.map((p, i) => r.blocked_week_indices.includes(i) ? 'PTO' : p),
      ];
      lines.push(row.join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `portfolio-heatmap-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]" data-testid="portfolio-heatmap-loading">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto py-12 px-4" data-testid="portfolio-heatmap-error">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-rose-500 mt-0.5" />
              <div>
                <div className="font-semibold text-slate-800">Couldn't load the portfolio heat-map</div>
                <div className="text-sm text-slate-600 mt-1">{error}</div>
                <Button className="mt-3" variant="outline" size="sm" onClick={() => load({ weeks })} data-testid="portfolio-heatmap-retry-btn">
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Retry
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full" data-testid="portfolio-heatmap-view">
      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider mb-1">
            <Flame className="w-3.5 h-3.5" /> Portfolio Commitment
          </div>
          <p className="text-sm text-slate-600 max-w-2xl">
            {rangeMode === 'custom' && data?.start_date && data?.end_date ? (
              <>Total committed FTE% per person from <span className="font-semibold">{data.start_date}</span> to <span className="font-semibold">{data.end_date}</span> ({data?.weeks_n ?? weeks} weeks) across every live project. Cells over 100% are over-allocated. Built from explicit assignments only.</>
            ) : (
              <>Total committed FTE% per person for the next {data?.weeks_n ?? weeks} weeks across every live project. Cells over 100% are over-allocated. Built from explicit assignments only.</>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {[12, 26, 52].map((n) => (
            <Button
              key={n}
              variant={rangeMode === 'preset' && weeks === n ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setRangeMode('preset'); setWeeks(n); }}
              data-testid={`portfolio-heatmap-weeks-${n}-btn`}
            >
              {n}w
            </Button>
          ))}
          <div className="flex items-center gap-1 border-l pl-2 ml-1">
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="h-8 text-xs border rounded px-2 bg-white"
              aria-label="Start date"
              data-testid="portfolio-heatmap-start-date"
            />
            <span className="text-xs text-slate-400">→</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="h-8 text-xs border rounded px-2 bg-white"
              aria-label="End date"
              data-testid="portfolio-heatmap-end-date"
            />
            <Button
              variant={rangeMode === 'custom' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setRangeMode('custom'); applyCustomRange(); }}
              disabled={!customStart || !customEnd}
              data-testid="portfolio-heatmap-apply-range-btn"
            >
              Apply
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={() => load({ weeks })} data-testid="portfolio-heatmap-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={onExportCsv} disabled={!resources.length} data-testid="portfolio-heatmap-export-csv-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              try {
                exportCapacityReport(data, {});
              } catch (e) {
                // best-effort; never crash the view on export
                console.error('Capacity report export failed', e);
              }
            }}
            disabled={!resources.length}
            data-testid="portfolio-heatmap-export-pdf-btn"
            title="Download a stakeholder-ready capacity report (PDF)"
          >
            <FileDown className="w-3.5 h-3.5 mr-1.5" /> Capacity report
          </Button>
        </div>
      </div>

      {/* v380 — Entity / Region filters (affect the grid + summary totals) */}
      {(erEntities.length > 0 || erRegions.length > 0) && (
        <div className="flex flex-wrap items-start gap-5 mb-5" data-testid="resource-entity-region-filters">
          <EntityRegionFilterChips
            entities={erEntities} regions={erRegions}
            entityFilter={entityFilter} regionFilter={regionFilter}
            setEntityFilter={setEntityFilter} setRegionFilter={setRegionFilter}
            idPrefix="resource" />
          {(entityFilter.length > 0 || regionFilter.length > 0) && (
            <button onClick={() => { setEntityFilter([]); setRegionFilter([]); }}
              className="text-[11px] text-slate-600 underline mt-4 hover:text-[#0F2040] px-1 py-0.5" data-testid="resource-filters-clear">clear</button>
          )}
        </div>
      )}

      {/* Summary chips */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <SummaryChip icon={<UsersIcon className="w-4 h-4 text-slate-500" />} label="People" value={summary.total_resources ?? 0} testid="portfolio-heatmap-summary-people" />
        <SummaryChip icon={<Layers className="w-4 h-4 text-slate-500" />} label="Projects in scope" value={summary.estimates_in_scope ?? 0} testid="portfolio-heatmap-summary-projects" />
        <SummaryChip icon={<Flame className="w-4 h-4 text-rose-500" />} label="Over-allocated people" value={summary.over_allocated_people ?? 0} tone={summary.over_allocated_people > 0 ? 'rose' : 'slate'} testid="portfolio-heatmap-summary-over-people" />
        <SummaryChip icon={<AlertTriangle className="w-4 h-4 text-rose-500" />} label="Over-allocated cells" value={summary.over_allocated_cells ?? 0} tone={summary.over_allocated_cells > 0 ? 'rose' : 'slate'} testid="portfolio-heatmap-summary-over-cells" />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 mb-4 text-xs text-slate-600">
        <LegendChip color="bg-slate-50 border-slate-200" label="Idle" />
        <LegendChip color="bg-emerald-50 border-emerald-200" label="Light (≤50%)" />
        <LegendChip color="bg-emerald-200 border-emerald-300" label="Healthy (≤85%)" />
        <LegendChip color="bg-amber-200 border-amber-300" label="Loaded (≤100%)" />
        <LegendChip color="bg-rose-300 border-rose-400" label="Over-allocated (>100%)" />
        <LegendChip color="bg-rose-500 border-rose-600" label="Severe (>150%)" />
        <LegendChip color="bg-slate-200 border-slate-300 bg-[repeating-linear-gradient(45deg,#cbd5e1_0,#cbd5e1_2px,transparent_2px,transparent_6px)]" label="PTO" />
      </div>

      {/* Empty state */}
      {resources.length === 0 ? (
        <Card data-testid="portfolio-heatmap-empty">
          <CardContent className="py-12 text-center text-slate-500">
            No committed assignments yet. Assign named resources with a % FTE on any estimate's
            <span className="font-semibold"> Resource Assignment Planner</span> to populate this heat-map.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="border-b">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Flame className="w-4 h-4 text-rose-500" />
              {summary.total_resources} {summary.total_resources === 1 ? 'person' : 'people'} · {data?.weeks_n} weeks · committed FTE%
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-xs border-collapse" data-testid="portfolio-heatmap-grid">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr>
                  <th className="sticky left-0 bg-slate-50 z-20 text-left px-3 py-2 border-b border-r font-semibold text-slate-700 min-w-[220px]">
                    Person
                  </th>
                  <th className="text-right px-2 py-2 border-b border-r font-semibold text-slate-700 whitespace-nowrap">
                    Peak
                  </th>
                  {data.week_labels.map((iso, i) => (
                    <th
                      key={iso}
                      className={`text-center px-1 py-2 border-b border-r font-medium text-slate-500 whitespace-nowrap ${i === data.today_idx ? 'bg-sky-50' : ''}`}
                      style={{ minWidth: 52 }}
                      data-testid={`portfolio-heatmap-week-header-${i}`}
                    >
                      <div className="text-[10px] text-slate-400">W{i + 1}</div>
                      <div>{fmtWeekLabel(iso)}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {resources.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50/60" data-testid={`portfolio-heatmap-row-${r.id}`}>
                    <td className="sticky left-0 bg-white hover:bg-slate-50/60 z-10 px-3 py-2 border-b border-r">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-[10px] font-semibold shrink-0">
                          {(r.name || '?').split(' ').map(p => p[0]).slice(0, 2).join('')}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-slate-800 truncate">{r.name}</div>
                          <div className="text-[11px] text-slate-500 flex items-center gap-1 truncate">
                            {(r.roles || []).map(rc => roleLabels[rc] || rc).join(' · ') || (
                              r.email && (<><Mail className="w-2.5 h-2.5" /><span className="truncate">{r.email}</span></>)
                            )}
                          </div>
                        </div>
                        {r.over_allocated_week_indices?.length > 0 && (
                          <Badge variant="outline" className="ml-auto text-rose-700 border-rose-300 bg-rose-50 text-[10px] px-1.5 py-0">
                            {r.over_allocated_week_indices.length}×
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className={`text-right px-2 py-2 border-b border-r font-semibold whitespace-nowrap ${r.peak_pct > 100 ? 'text-rose-700' : 'text-slate-700'}`}>
                      {Math.round(r.peak_pct)}%
                    </td>
                    {r.weekly_fte_pct.map((pct, i) => {
                      const blocked = r.blocked_week_indices.includes(i);
                      const style = cellStyleFor({ blocked, pct });
                      const over = !blocked && pct > 100;
                      const projects = r.weekly_projects?.[i] || [];
                      const isHover = hoverCell && hoverCell.resourceId === r.id && hoverCell.weekIdx === i;
                      return (
                        <td
                          key={i}
                          className={`relative text-center border-b border-r transition ${style.bg} ${style.border} ${style.pattern || ''} ${over ? 'cursor-pointer ring-1 ring-inset ring-rose-400/50 hover:ring-2 hover:ring-rose-500' : (pct > 0 || blocked) ? 'hover:ring-2 hover:ring-slate-400/40' : ''}`}
                          style={{ minWidth: 52, height: 36 }}
                          onClick={() => {
                            if (!over) return;
                            setReassignTarget({ resourceId: r.id, weekIso: data.week_labels[i] });
                          }}
                          onMouseEnter={() => setHoverCell({ resourceId: r.id, weekIdx: i, projects, pct, blocked, over, label: style.label })}
                          onMouseLeave={() => setHoverCell(null)}
                          data-testid={`portfolio-heatmap-cell-${r.id}-${i}`}
                          data-blocked={blocked ? '1' : '0'}
                          data-over-allocated={over ? '1' : '0'}
                        >
                          <span className={`text-[10px] font-medium ${style.text}`}>
                            {blocked ? 'PTO' : (pct > 0 ? `${Math.round(pct)}%` : '')}
                          </span>
                          {over && (
                            <Flame className="absolute top-0.5 right-0.5 w-2.5 h-2.5 text-rose-700/80 animate-pulse" aria-hidden="true" />
                          )}
                          {isHover && (pct > 0 || blocked) && (
                            <div className="absolute z-30 left-1/2 -translate-x-1/2 top-full mt-1 bg-slate-900 text-white text-[11px] rounded shadow-lg px-2.5 py-1.5 min-w-[180px] text-left pointer-events-none">
                              <div className="font-semibold">{r.name} · W{i + 1}</div>
                              <div className="text-slate-300">{data.week_labels[i]}</div>
                              {blocked ? (
                                <div className="text-amber-300 mt-0.5">Blocked (PTO)</div>
                              ) : (
                                <>
                                  <div className={`mt-0.5 font-semibold ${pct > 100 ? 'text-rose-300' : 'text-emerald-300'}`}>
                                    {Math.round(pct)}% committed · {style.label}
                                  </div>
                                  <div className="mt-1 border-t border-slate-700 pt-1 space-y-0.5">
                                    {projects.length === 0 ? (
                                      <div className="text-slate-400">No project breakdown</div>
                                    ) : projects.map((p) => (
                                      <div key={p.estimate_id} className="flex items-center justify-between gap-3">
                                        <span className="truncate max-w-[160px] text-slate-200">{p.name}</span>
                                        <span className="text-slate-300 shrink-0">{Math.round(p.pct)}%</span>
                                      </div>
                                    ))}
                                  </div>
                                  {over && (
                                    <div className="mt-1 pt-1 border-t border-slate-700 flex items-center gap-1 text-amber-300">
                                      <Sparkles className="w-3 h-3" /> Click to rebalance
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <SuggestReassignmentDialog
        open={!!reassignTarget}
        resourceId={reassignTarget?.resourceId}
        weekIso={reassignTarget?.weekIso}
        defaultExplicitOnly={true}
        onClose={() => setReassignTarget(null)}
        onSaved={() => { setReassignTarget(null); load({ weeks }); }}
      />
    </div>
  );
}

function SummaryChip({ icon, label, value, tone = 'slate', testid }) {
  const toneCls = tone === 'rose' ? 'border-rose-200 bg-rose-50' : 'border-slate-200 bg-white';
  return (
    <div className={`flex items-center gap-3 rounded-lg border ${toneCls} px-3 py-2`} data-testid={testid}>
      {icon}
      <div className="leading-tight">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-lg font-semibold text-slate-900">{value}</div>
      </div>
    </div>
  );
}

function LegendChip({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block w-4 h-4 rounded border ${color}`} />
      <span>{label}</span>
    </span>
  );
}
